/**
 * What somebody did to a table's look, carried over when CSRD changes its layout.
 *
 * A table read from CSRD is dressed by hand: a bold blue band on the totals, a
 * heading's fill, figures centred. While CSRD keeps the table's shape that is
 * kept cell by cell, where it was put. When CSRD adds a row or a column the
 * grid is drawn again, and with nothing to say which new cell is which old one
 * the dressing would be lost, which is the reason nobody would dress a table
 * that can be refreshed.
 *
 * So the dressing is followed by what a reader would follow it by: a row by its
 * label, a column by its heading. CSRD hands over no identifier for either, but
 * a label and a heading are what a statement is read by, and they hold from one
 * reading to the next. A heading that changed (2025 became 2026) is taken to be
 * the column that is left over where it stood.
 *
 * Only how a cell looks travels: never what it says, and never whether it is a
 * figure, which CSRD decides. Pure; nothing here knows of CSRD but its shape.
 */

import type { CsrdBlock } from '@/api/csrd'
import type { TableCell, TableSection } from '@/domain/section'
import { cellFrom } from '@/features/import/csrd'

/** What a cell can be dressed with: emphasis, a fill, an ink, an alignment, a rule above or under. */
type Dressing = Pick<TableCell, 'strong' | 'fill' | 'ink' | 'align' | 'ruleAbove' | 'ruleBelow'>

/** A label or a heading as a reader compares it: without case, accents or stray spacing. */
export function readingOf(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

/** Where each of `next` stood in `old`: by the words first, then the leftovers in order. */
export function pairedBy(
  old: readonly string[],
  next: readonly string[],
  byPosition: boolean,
): (number | undefined)[] {
  const waiting = new Map<string, number[]>()
  old.forEach((value, at) => {
    const key = readingOf(value)
    if (key) waiting.set(key, [...(waiting.get(key) ?? []), at])
  })

  const taken = new Set<number>()
  const paired = next.map((value) => {
    const at = waiting.get(readingOf(value))?.shift()
    if (at !== undefined) taken.add(at)
    return at
  })
  if (!byPosition) return paired

  const left = old.map((_, at) => at).filter((at) => !taken.has(at))
  return paired.map((at) => at ?? left.shift())
}

function dressingOf(cell: TableCell | undefined): Dressing {
  const dressing: Dressing = {}
  if (!cell) return dressing
  if (cell.strong) dressing.strong = true
  if (cell.fill) dressing.fill = cell.fill
  if (cell.ink) dressing.ink = cell.ink
  if (cell.align) dressing.align = cell.align
  if (cell.ruleAbove) dressing.ruleAbove = true
  if (cell.ruleBelow) dressing.ruleBelow = true
  return dressing
}

/** What a whole row wears wherever it has no cell to say: its first cell's emphasis and colours, not its alignment. */
function rowDressing(row: readonly TableCell[] | undefined): Dressing {
  const { align: _own, ...rest } = dressingOf(row?.[0])
  return rest
}

function dressed(incoming: TableCell, dressing: Dressing): TableCell {
  return { ...incoming, ...dressing }
}

/**
 * The widths of the old columns carried to the new ones, still adding up to a whole.
 *
 * A column with no old one to follow takes the average share. Nothing when the
 * old table never set any, or set them for another count of columns.
 */
function widthsFor(section: TableSection, columns: readonly (number | undefined)[]): number[] {
  const old = section.columnWidths
  if (old.length !== section.header.length || old.length === 0 || columns.length === 0) return []

  const average = old.reduce((sum, width) => sum + width, 0) / old.length
  const shares = columns.map((at) => (at === undefined ? average : (old[at] ?? average)))
  const total = shares.reduce((sum, width) => sum + width, 0)
  return total > 0 ? shares.map((share) => Math.round((share / total) * 1000) / 10) : []
}

/** The table CSRD holds now, dressed the way the one in the report was. */
export function restyled(
  section: TableSection,
  table: CsrdBlock,
): Pick<TableSection, 'header' | 'rows' | 'columnWidths'> {
  const columns = pairedBy(
    section.header.map((cell) => cell.value),
    table.header.map((cell) => cell.value),
    true,
  )

  const header = table.header.map((incoming, at) => {
    const was = columns[at]
    return dressed(
      cellFrom(incoming),
      dressingOf(was === undefined ? undefined : section.header[was]),
    )
  })

  // A row is followed by its label alone: a row that is not there to be found is not dressed at random.
  const rowsAt = pairedBy(
    section.rows.map((row) => row[0]?.value ?? ''),
    table.rows.map((row) => row[0]?.value ?? ''),
    false,
  )

  const rows = table.rows.map((row, at) => {
    const was = rowsAt[at] === undefined ? undefined : section.rows[rowsAt[at]]
    return row.map((incoming, column) => {
      if (!was) return cellFrom(incoming)
      const old = columns[column]
      const own = old === undefined ? undefined : was[old]
      return dressed(cellFrom(incoming), own ? dressingOf(own) : rowDressing(was))
    })
  })

  return { header, rows, columnWidths: widthsFor(section, columns) }
}
